<template>
    <v-app>
        <exchange-rate-setup :dialog="dialog" :data="data" @update:dialog="dialog = $event" />
        <v-container fluid>
            <v-row>
                <v-col cols="12">
                    <v-expansion-panels v-model="panel">
                        <v-expansion-panel>
                            <v-expansion-panel-title color="blue-grey-lighten-4" class="panel-header">
                                Exchange Rates List
                            </v-expansion-panel-title>
                            <v-expansion-panel-text>
                                <v-row class="py-2">
                                    <v-col md="4" cols="12">
                                        <v-text-field
                                            v-model="search"
                                            append-inner-icon="mdi-magnify"
                                            label="Search"
                                            single-line
                                            hide-details
                                            density="compact"
                                            variant="outlined"
                                            bg-color="grey-lighten-4"
                                            class="pl-2"
                                            clearable
                                        ></v-text-field>
                                    </v-col>

                                    <v-col md="5" cols="12">
                                        <div class="d-flex align-center flex-column">
                                            <v-btn-group variant="outlined" density="compact" size="x-small" divided>
                                                <v-btn prepend-icon="mdi mdi-download" variant="flat" color="success" @click="exportToCSV">CSV</v-btn>
                                                <v-btn prepend-icon="mdi mdi-download" variant="flat" color="primary" @click="exportToExcel">EXCEL</v-btn>
                                                <v-btn prepend-icon="mdi mdi-download" variant="flat" color="red" @click="exportToPDF">PDF</v-btn>
                                            </v-btn-group>
                                        </div>
                                    </v-col>

                                    <v-col md="3" cols="12">
                                        <div class="text-right pr-2">
                                            <v-btn
                                                v-if="$insertAccess($route.fullPath)"
                                                @click="openAddItem"
                                                color="primary"
                                                variant="tonal"
                                                prepend-icon="mdi mdi-plus"
                                                rounded="xl"
                                            >
                                                Add Exchange Rate
                                            </v-btn>
                                        </div>
                                    </v-col>
                                </v-row>

                                <v-data-table
                                    :headers="headers"
                                    :items="getSchema"
                                    :loading="loading"
                                    v-model:items-per-page="itemsPerPage"
                                    :search="search"
                                    :hover="true"
                                    density="compact"
                                >
                                    <template v-slot:item.index="{ index }">
                                        {{ ++index }}
                                    </template>

                                    <template v-slot:item.rate="{ item }">
                                        {{ parseFloat(item.rate).toFixed(4) }}
                                    </template>

                                    <template v-slot:item.type="{ item }">
                                        <v-chip 
                                            :color="item.type == 'import' ? 'blue' : 'orange'" 
                                            size="small" 
                                            class="text-capitalize"
                                        >
                                            {{ item.type }}
                                        </v-chip>
                                    </template>

                                    <template v-slot:item.status="{ item }">
                                        <v-chip v-if="item.status == 1" color="success" size="small" class="text-capitalize">Active</v-chip>
                                        <v-chip v-else color="red" size="small" class="text-capitalize">Inactive</v-chip>
                                    </template>

                                    <template v-slot:item.actions="{ item }">
                                        <v-icon
                                            class="me-2 edit-button"
                                            color="primary"
                                            @click="editItem(item)"
                                            v-if="$updateAccess($route.fullPath)"
                                            title="Edit"
                                        >
                                            mdi mdi-pencil-outline
                                        </v-icon>
                                    </template>
                                </v-data-table>
                            </v-expansion-panel-text>
                        </v-expansion-panel>
                    </v-expansion-panels>
                </v-col>
            </v-row>
        </v-container>
    </v-app>
</template>

<script>
import exchangeRateSetup from '@/components/module/inventory/settingsManagement/exchangeRateSetup.vue';

export default {
    components: {
        exchangeRateSetup
    },
    data: () => ({
        itemsPerPage: 10,
        panel: [0],
        loading: false,
        search: "",
        dialog: false,
        data: {},
        headers: [
            { title: "#SL", align: "center", key: "index", width: "5%", sortable: false },
            { title: "Currency", key: "currency_name", align: "left", sortable: false },
            { title: "Rate", key: "rate", align: "right", sortable: false },
            // { title: "Type", key: "type", align: "center", sortable: false },
            { title: "Rate Date", key: "rate_date", align: "center", sortable: false },
            { title: "Status", align: "center", key: "status", sortable: true },
            { title: "Actions", align: "center", key: "actions", sortable: false }
        ],
    }),
    created() {
        if (this.$store.getters.getExchangeRateList.length == 0) {
            this.loading = true;
            this.$store.dispatch('getExchangeRateList').then(() => {
                this.loading = false;
            }).catch(() => {
                this.loading = false;
            });
        }
        // Load currency list
        if (this.$store.getters.getCurrencyList.length == 0) {
            this.$store.dispatch('getCurrencyList');
        }
    },
    computed: {
        getSchema() {
            const rates = this.$store.getters.getExchangeRateList || [];
            return rates.map(rate => ({
                ...rate,
                currency_name: rate.currency ? `${rate.currency.symbol} ${rate.currency.name} (${rate.currency.code})` : 'N/A'
            }));
        },
        getCurrencyList() {
            return this.$store.getters.getCurrencyList || [];
        },
    },
    methods: {
        openAddItem() {
            // Load currencies if not already loaded (async)
            if (this.$store.getters.getCurrencyList.length == 0) {
                this.$store.dispatch('getCurrencyList').catch((error) => {
                    console.error('Failed to load currencies:', error);
                });
            }
            
            // Open dialog immediately
            this.data = {
                id: '',
                currency_id: '',
                rate: '',
                type: '',
                rate_date: new Date(),
                remarks: '',
                status: 1,
            };
            this.dialog = true;
        },
        editItem(item) {
            // Load currencies if not already loaded
            if (this.$store.getters.getCurrencyList.length == 0) {
                this.$store.dispatch('getCurrencyList');
            }
            this.data = { ...item };
            this.dialog = true;
        },
        exportToPDF() {
            let serialNumber = 1;
            const columns = ['#SL', 'Currency', 'Rate', 'Type', 'Rate Date', 'Status'];
            const rows = this.getSchema.map(item => [
                serialNumber++,
                item.currency_name,
                item.rate || '',
                item.type || '',
                item.rate_date || '',
                item.status == 1 ? 'Active' : 'Inactive'
            ]);
            this.$exportTableToPDF(columns, rows, 'Exchange Rates List', 'exchange_rates_list_' + this.$fileDate());
        },
        exportToExcel() {
            var data = [['#SL', 'Currency', 'Rate', 'Type', 'Rate Date', 'Status']];
            var serialNumber = 1;
            this.getSchema.forEach(item => {
                data.push([
                    serialNumber++,
                    item.currency_name,
                    item.rate || '',
                    item.type || '',
                    item.rate_date || '',
                    item.status == 1 ? 'Active' : 'Inactive'
                ]);
            });
            this.$exportTableToExcel(data, 'exchange_rates_list_' + this.$fileDate());
        },
        exportToCSV() {
            var data = [['#SL', 'Currency', 'Rate', 'Type', 'Rate Date', 'Status']];
            var serialNumber = 1;
            this.getSchema.forEach(item => {
                data.push([
                    serialNumber++,
                    item.currency_name,
                    item.rate || '',
                    item.type || '',
                    item.rate_date || '',
                    item.status == 1 ? 'Active' : 'Inactive'
                ]);
            });
            this.$exportTableToCSV(data, 'exchange_rates_list_' + this.$fileDate());
        },
    },
}
</script>

<style scoped>
.panel-header {
    font-weight: 600;
}

.edit-button {
    cursor: pointer;
}

.edit-button:hover {
    transform: scale(1.2);
}
</style>

